import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { es, enGB, fr, de } from "date-fns/locale";
import { format } from "date-fns";
import { toast } from "sonner";
import { Minus, Plus, MagnifyingGlass } from "@phosphor-icons/react";

const LOCALES = { es, en: enGB, fr, de };

export const SearchBar = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [range, setRange] = useState();
  const [guests, setGuests] = useState(2);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const locale = LOCALES[(i18n.language || "es").slice(0, 2)] || es;

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const submit = () => {
    if (!range?.from || !range?.to) {
      setOpen(true);
      return toast.error(t("booking.errDates"));
    }
    navigate(`/disponibilidad?checkin=${format(range.from, "yyyy-MM-dd")}&checkout=${format(range.to, "yyyy-MM-dd")}&guests=${guests}`);
  };

  const fmt = (d) => format(d, "dd MMM yy", { locale });

  return (
    <div data-testid="booking-search-bar" className="relative z-30 max-w-[1100px] mx-auto px-6 md:px-10 -mt-12 md:-mt-16">
      <div ref={ref} className="relative bg-white border border-line shadow-2xl">
        <div className="grid grid-cols-2 md:grid-cols-[1.4fr_1fr_auto]">
          <button
            data-testid="search-dates-btn"
            onClick={() => setOpen(!open)}
            className="col-span-2 md:col-span-1 text-left px-6 py-5 border-b md:border-b-0 md:border-r border-line hover:bg-sand/60 transition-colors"
          >
            <span className="block text-[10px] uppercase tracking-[0.2em] text-ink/45">
              {t("booking.checkin")} → {t("booking.checkout")}
            </span>
            <span className="block mt-1 text-sm text-ink">
              {range?.from ? fmt(range.from) : "—"} <span className="text-terracotta mx-1">→</span> {range?.to ? fmt(range.to) : "—"}
            </span>
          </button>

          <div className="px-6 py-5 flex items-center justify-between border-r border-line">
            <span className="text-[10px] uppercase tracking-[0.2em] text-ink/45">{t("detail.guests")}</span>
            <span className="flex items-center gap-3">
              <button data-testid="search-guests-minus" onClick={() => setGuests(Math.max(1, guests - 1))} className="w-7 h-7 border border-line flex items-center justify-center text-ink hover:border-terracotta hover:text-terracotta transition-colors" aria-label="-">
                <Minus size={13} />
              </button>
              <span data-testid="search-guests-count" className="text-sm w-4 text-center">{guests}</span>
              <button data-testid="search-guests-plus" onClick={() => setGuests(Math.min(8, guests + 1))} className="w-7 h-7 border border-line flex items-center justify-center text-ink hover:border-terracotta hover:text-terracotta transition-colors" aria-label="+">
                <Plus size={13} />
              </button>
            </span>
          </div>

          <button
            data-testid="search-submit-btn"
            onClick={submit}
            className="col-span-2 md:col-span-1 px-8 py-5 bg-terracotta text-sand text-xs uppercase tracking-[0.2em] hover:bg-atlantic transition-colors duration-300 flex items-center justify-center gap-2"
          >
            <MagnifyingGlass size={16} weight="bold" />
            {t("search.cta")}
          </button>
        </div>

        {open && (
          <div data-testid="search-calendar-popover" className="absolute left-0 right-0 md:right-auto top-full mt-1 bg-white border border-line shadow-2xl p-4 z-40">
            <DayPicker
              mode="range"
              selected={range}
              onSelect={(r) => {
                setRange(r);
                if (r?.from && r?.to) setOpen(false);
              }}
              numberOfMonths={1}
              locale={locale}
              disabled={[{ before: new Date() }]}
              showOutsideDays={false}
            />
          </div>
        )}
      </div>
    </div>
  );
};
